import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

const configElement = document.querySelector('#gelrova-anniversary-gift-config');

if (configElement instanceof HTMLElement && !window.GelrovaAnniversaryGift) {
  const source = 'gelrova-anniversary-gift';
  const statusElement = document.querySelector('#gelrova-anniversary-gift-status');
  const campaign = configElement.dataset.campaign || '2026';
  const giftVariantId = Number(configElement.dataset.giftVariantId);
  const eligibleProductIds = new Set(
    (configElement.dataset.eligibleProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  );
  const startsAt = Date.parse(configElement.dataset.startsAt || '');
  const endsAt = Date.parse(configElement.dataset.endsAt || '');
  const isUs = configElement.dataset.isUs === 'true';
  let syncing = false;
  let queued = false;
  let queueTimer;

  const announce = (message) => {
    if (!(statusElement instanceof HTMLElement) || !message) return;
    statusElement.textContent = '';
    window.requestAnimationFrame(() => {
      statusElement.textContent = message;
    });
  };

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Cart request failed');
    }
    return data;
  };

  const getCart = () => requestJson(configElement.dataset.cartUrl);

  const dispatchCartEvent = async (response, eventType) => {
    const cart = await getCart();
    const EventClass = eventType === 'add' ? CartAddEvent : CartUpdateEvent;
    document.dispatchEvent(
      new EventClass(cart, configElement.id, {
        source,
        itemCount: cart.item_count,
        sections: response.sections || {},
        variantId: String(giftVariantId),
      })
    );
  };

  const mutateGift = async ({ action, lineKey }) => {
    const sections = sectionIds();
    const shared = {
      sections,
      sections_url: window.location.pathname + window.location.search,
    };

    if (action === 'add') {
      const response = await requestJson(configElement.dataset.cartAddUrl, {
        method: 'POST',
        body: JSON.stringify({
          items: [
            {
              id: giftVariantId,
              quantity: 1,
              properties: { _gelrova_anniversary_gift: campaign },
            },
          ],
          ...shared,
        }),
      });
      await dispatchCartEvent(response, 'add');
      announce(configElement.dataset.addedMessage);
      return;
    }

    const response = await requestJson(configElement.dataset.cartChangeUrl, {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: action === 'normalize' ? 1 : 0, ...shared }),
    });
    await dispatchCartEvent(response, 'update');
  };

  const sync = async () => {
    if (syncing || !giftVariantId || eligibleProductIds.size === 0) {
      queued = true;
      return;
    }

    syncing = true;
    queued = false;
    try {
      const cart = await getCart();
      const now = Date.now();
      const isCampaignActive = now >= startsAt && now <= endsAt;
      const hasQualifyingBag = cart.items.some((item) => eligibleProductIds.has(String(item.product_id)));
      const giftLines = cart.items.filter(
        (item) =>
          Number(item.variant_id) === giftVariantId &&
          String(item.properties?._gelrova_anniversary_gift || '') === campaign
      );
      const shouldHaveGift = isCampaignActive && isUs && hasQualifyingBag;

      if (shouldHaveGift && giftLines.length === 0) {
        await mutateGift({ action: 'add' });
        return;
      }

      if (shouldHaveGift && giftLines[0]?.quantity !== 1) {
        await mutateGift({ action: 'normalize', lineKey: giftLines[0].key });
      }

      if (shouldHaveGift && giftLines.length > 1) {
        for (const extraLine of giftLines.slice(1)) {
          await mutateGift({ action: 'remove', lineKey: extraLine.key });
        }
      }

      if (!shouldHaveGift && giftLines.length > 0) {
        for (const giftLine of giftLines) {
          await mutateGift({ action: 'remove', lineKey: giftLine.key });
        }
        announce(
          !isUs && isCampaignActive && hasQualifyingBag
            ? configElement.dataset.regionMessage
            : configElement.dataset.removedMessage
        );
      }
    } catch (error) {
      console.error('[Gelrova anniversary gift]', error);
      announce(configElement.dataset.errorMessage);
    } finally {
      syncing = false;
      if (queued) scheduleSync(120);
    }
  };

  const scheduleSync = (delay = 80) => {
    window.clearTimeout(queueTimer);
    queueTimer = window.setTimeout(sync, delay);
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === source) return;
    scheduleSync(120);
  });
  document.addEventListener('cart:updated', () => scheduleSync(120));
  document.addEventListener('shopify:section:load', () => scheduleSync(80));
  window.addEventListener('pageshow', () => scheduleSync(80));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleSync(80);
  });

  window.GelrovaAnniversaryGift = { sync: () => scheduleSync(0) };
  scheduleSync(120);
}
